import {sendEvent} from '@github-ui/hydro-analytics'
import {FileDiffIcon, GitPullRequestIcon, type Icon} from '@primer/octicons-react'
import {Button, ButtonGroup, Spinner} from '@primer/react'
import {useEffect} from 'react'

import {useCreatePullRequestForTask} from '../../hooks/use-create-pull-request-for-task'
import styles from './PullRequestButton.module.css'

interface PullRequestButtonProps {
  pullUrl?: string
  repoNwo: string
  baseRef?: string
  headRef?: string
  taskId?: string
  disabled?: boolean
  onError?: (message: string) => void
}

export function PullRequestButton({
  pullUrl,
  taskId,
  repoNwo,
  baseRef,
  headRef,
  disabled = false,
  onError,
}: PullRequestButtonProps) {
  const {createPullRequest, isLoading} = useCreatePullRequestForTask()

  const showCreatePrButton = !pullUrl && Boolean(baseRef) && Boolean(headRef)

  useEffect(() => {
    if (showCreatePrButton) {
      sendEvent('agent_sessions.task_view', {
        target: 'CREATE_PULL_REQUEST_BUTTON_RENDER',
        task_id: taskId,
      })
    }
  }, [showCreatePrButton, taskId])

  const handleViewPRClick = () => {
    sendEvent('agent_sessions.task_view', {
      target: 'VIEW_PULL_REQUEST_BUTTON',
      pull_url: pullUrl,
      task_id: taskId,
    })
  }

  const handleViewDiffClick = () => {
    sendEvent('agent_sessions.task_view', {
      target: 'VIEW_PULL_REQUEST_DIFF_BUTTON',
      pull_url: pullUrl,
      task_id: taskId,
    })
  }

  const handleCreatePRViaAPI = () => {
    if (!taskId) return

    sendEvent('agent_sessions.task_view', {
      target: 'CREATE_PULL_REQUEST_BUTTON',
      task_id: taskId,
    })

    createPullRequest(
      {repoNwo, taskId},
      {
        onSuccess: response => {
          window.location.href = `/${repoNwo}/pull/${response.number}`
        },
        onError: error => {
          onError?.(error instanceof Error ? error.message : 'Failed to create pull request')
        },
      },
    )
  }

  return pullUrl ? (
    <ButtonGroup>
      <ButtonComponent
        url={pullUrl}
        icon={<GitPullRequestIcon />}
        onClick={handleViewPRClick}
        label="View pull request"
      />
      <ButtonComponent
        url={`${pullUrl}/files`}
        icon={<FileDiffIcon className={styles.diffIcon} />}
        onClick={handleViewDiffClick}
        ariaLabel="View pull request files"
      />
    </ButtonGroup>
  ) : baseRef && headRef && taskId ? (
    <Button
      className={styles.button}
      leadingVisual={isLoading ? () => <Spinner size="small" /> : GitPullRequestIcon}
      variant="primary"
      size="small"
      onClick={handleCreatePRViaAPI}
      aria-label="Create pull request"
      aria-disabled={disabled || isLoading}
      inactive={disabled || isLoading}
      disabled={disabled || isLoading}
    >
      Create pull request
    </Button>
  ) : null
}

function ButtonComponent({
  url,
  icon,
  onClick,
  label,
  ariaLabel,
  disabled = false,
}: {
  url: string
  icon: React.ReactElement | Icon
  onClick: () => void
  label?: string
  ariaLabel?: string
  disabled?: boolean
}) {
  return (
    <Button
      as="a"
      href={disabled ? undefined : url}
      target="_self"
      className={styles.button}
      leadingVisual={icon}
      variant="primary"
      size="small"
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel || label}
      aria-disabled={disabled}
      inactive={disabled}
      tabIndex={disabled ? 0 : undefined}
    >
      {label}
    </Button>
  )
}
